/**
 * Toast store — global hors React, consommable depuis n'importe où
 * (lib/api.ts, contexts, components). Pas de dépendance externe.
 *
 * API publique :
 *   toast.success("Sauvegardé")
 *   toast.error("Échec connexion", { duration: 8000 })
 *   toast.info("Token expiré", { id: "session-expired" })   // dedupe par id
 *   toast.dismiss(id)
 *   toast.clear()
 *
 * Le hook useToasts() (consommé par <ToastContainer>) utilise
 * useSyncExternalStore pour rester en phase avec le store sans re-render
 * inutile.
 */

import { useSyncExternalStore } from "react";

export type ToastKind = "success" | "error" | "info";

export type ToastItem = {
  id: string;
  kind: ToastKind;
  message: string;
  /** ms avant auto-dismiss. 0 = sticky. Default: 5000. */
  duration: number;
};

export type ToastOptions = {
  id?: string;
  duration?: number;
};

const DEFAULT_DURATION = 5000;

let items: ToastItem[] = [];
const listeners = new Set<() => void>();
const timers = new Map<string, ReturnType<typeof setTimeout>>();

function emit() {
  for (const l of listeners) l();
}

function subscribe(l: () => void): () => void {
  listeners.add(l);
  return () => listeners.delete(l);
}

function getSnapshot(): ToastItem[] {
  return items;
}

function nextId(): string {
  return `t_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

function push(kind: ToastKind, message: string, options?: ToastOptions): string {
  const id = options?.id ?? nextId();
  const duration = options?.duration ?? DEFAULT_DURATION;

  // Dedupe : si un toast avec le même id existe déjà, on le remplace.
  const existingTimer = timers.get(id);
  if (existingTimer) clearTimeout(existingTimer);

  const item: ToastItem = { id, kind, message, duration };
  items = [...items.filter((t) => t.id !== id), item];

  if (duration > 0) {
    const handle = setTimeout(() => dismiss(id), duration);
    timers.set(id, handle);
  }

  emit();
  return id;
}

function dismiss(id: string) {
  const handle = timers.get(id);
  if (handle) {
    clearTimeout(handle);
    timers.delete(id);
  }
  const before = items.length;
  items = items.filter((t) => t.id !== id);
  if (items.length !== before) emit();
}

function clear() {
  for (const handle of timers.values()) clearTimeout(handle);
  timers.clear();
  if (items.length > 0) {
    items = [];
    emit();
  }
}

export const toast = {
  success: (message: string, options?: ToastOptions) => push("success", message, options),
  error: (message: string, options?: ToastOptions) => push("error", message, options),
  info: (message: string, options?: ToastOptions) => push("info", message, options),
  dismiss,
  clear,
};

export function useToasts(): ToastItem[] {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}
